import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Link2, Archive, ArchiveRestore, ArrowRightLeft, AlertTriangle, Loader2, CheckCircle2, XCircle, Copy } from "lucide-react";
import { API, api } from "@/lib/api";
import { Card, Spin, fmtDateTime, formatApiError } from "./bits";

const selCls = "text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white min-w-[220px] max-w-[320px]";
const actionBtn = "flex items-center gap-1.5 text-xs font-medium border rounded-lg px-3 py-1.5 transition-colors disabled:opacity-40";

const SectionTitle = ({ children, count }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-2">
    {children}
    {count !== undefined && <span className="text-[10px] font-bold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{count}</span>}
  </h3>
);

const VehicleCell = ({ v }) => (
  <div className="min-w-0">
    <div className="text-xs font-medium text-gray-900 truncate">{v.label || `Véhicule ${v.tracker_id ?? "?"}`}</div>
    <div className="text-[10px] text-gray-400 truncate">
      {[v.reg_number, v.tracker_id != null ? `boîtier ${v.tracker_id}` : "sans boîtier", v.source].filter(Boolean).join(" · ")}
    </div>
  </div>
);

export default function ReconciliationSection({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {title, lines, confirmLabel, danger, onConfirm}
  const [attachSel, setAttachSel] = useState({});
  const [reassignSel, setReassignSel] = useState({});

  const base = `${API}/admin/clients/${clientId}/reconciliation`;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const r = await api.get(base); setData(r.data); }
    catch (e) { setError(formatApiError(e)); }
    setLoading(false);
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true); setError(""); setNotice("");
    try { await fn(); if (okMsg) setNotice(okMsg); setConfirm(null); await load(); }
    catch (e) { setError(formatApiError(e)); setConfirm(null); }
    setBusy(false);
  };

  if (loading && !data) return <Spin />;
  if (!data) return <div className="text-xs text-red-600" data-testid="recon-load-error">{error || "Chargement impossible"}</div>;

  const targets = (data.attach_targets || []);
  const reassignable = (data.reassignable_trackers || []);

  return (
    <div className="space-y-4" data-testid="sa-detail-reconciliation">
      {/* En-tête : état sync + rafraîchir */}
      <Card className="p-4 flex flex-wrap items-center gap-3">
        <div className="text-sm font-medium text-gray-700">Réconciliation véhicules</div>
        {data.navixy_available ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="recon-sync-ok">
            <CheckCircle2 size={13} /> Registre synchronisé ({data.counts.active} actifs)
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-red-600" data-testid="recon-sync-error">
            <XCircle size={13} /> Navixy indisponible — état du registre local ({data.sync?.error || "erreur"})
          </span>
        )}
        <span className="text-[11px] text-gray-400">
          {data.counts.tracker_missing} boîtier(s) manquant(s) · {data.counts.orphans} fiche(s) orpheline(s) · {data.counts.archived} archivé(s)
        </span>
        <button onClick={load} disabled={loading || busy}
          className={`${actionBtn} border-gray-200 text-gray-600 hover:bg-gray-50 ml-auto`} data-testid="recon-refresh">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualiser
        </button>
      </Card>

      {error && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="recon-error">{error}</div>}
      {notice && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="recon-notice">{notice}</div>}

      {/* Option badge Boîtier manquant (par tenant, désactivée par défaut) */}
      <Card className="p-4" data-testid="recon-badge-card">
        <SectionTitle>Badge « Boîtier manquant » sur le Dashboard client</SectionTitle>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={!!data.show_missing_tracker_badge} disabled={busy}
            onChange={(e) => run(() => api.put(`${base}/badge-option`, { show_missing_tracker_badge: e.target.checked }),
              e.target.checked ? "Badge « Boîtier manquant » activé pour ce client" : "Badge « Boîtier manquant » désactivé")}
            data-testid="recon-badge-toggle" />
          <span className="text-xs text-gray-600">
            Si activé, les véhicules au boîtier manquant apparaissent dans les « Actions prioritaires » du Dashboard client
            avec le badge <strong>Boîtier manquant</strong> (et dans le PDF quotidien). Désactivé par défaut.
            Ces véhicules restent toujours visibles ici et dans le contrôle d'intégrité, option activée ou non.
          </span>
        </label>
      </Card>

      {/* Fiches orphelines → rattachement manuel */}
      <Card className="p-4" data-testid="recon-orphans-card">
        <SectionTitle count={data.orphan_admin_records.length}>Fiches administratives orphelines</SectionTitle>
        {data.orphan_admin_records.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="recon-orphans-empty">Aucune fiche orpheline — toutes les fiches sont rattachées à un véhicule du registre.</p>
        ) : data.orphan_admin_records.map((o) => {
          const sel = attachSel[o.tracker_id] || "";
          const target = targets.find((t) => t.uid === sel);
          return (
            <div key={o.tracker_id} className="flex flex-wrap items-center gap-3 py-2.5 border-b border-gray-50 last:border-0" data-testid={`recon-orphan-row-${o.tracker_id}`}>
              <div className="min-w-[180px]">
                <div className="text-xs font-medium text-gray-900">Fiche du boîtier {o.tracker_id}</div>
                <div className="text-[10px] text-gray-400">
                  {[o.sections.length ? `sections : ${o.sections.join(", ")}` : null,
                    o.controles ? `${o.controles} contrôle(s)` : null,
                    o.etat_des_lieux ? `${o.etat_des_lieux} état(s) des lieux` : null,
                    o.documents ? `${o.documents} document(s)` : null].filter(Boolean).join(" · ") || "fiche vide"}
                  {o.updated_at ? ` · modifiée le ${fmtDateTime(o.updated_at)}` : ""}
                </div>
              </div>
              <select value={sel} onChange={(e) => setAttachSel({ ...attachSel, [o.tracker_id]: e.target.value })}
                className={selCls} data-testid={`recon-orphan-target-${o.tracker_id}`}>
                <option value="">Rattacher à un véhicule…</option>
                {targets.map((t) => (
                  <option key={t.uid} value={t.uid} disabled={t.has_admin_record}>
                    {t.label || `Véhicule ${t.tracker_id}`}{t.reg_number ? ` — ${t.reg_number}` : ""} (boîtier {t.tracker_id}){t.has_admin_record ? " — fiche existante" : ""}
                  </option>
                ))}
              </select>
              <button disabled={!sel || busy}
                onClick={() => setConfirm({
                  title: "Rattacher la fiche orpheline",
                  lines: [
                    `Fiche du boîtier ${o.tracker_id} → « ${target?.label || sel} » (boîtier ${target?.tracker_id}).`,
                    "Les documents de la fiche suivent le véhicule ; les fichiers restent à leur emplacement d'origine.",
                    "Action auditée, historique conservé — aucune donnée supprimée.",
                  ],
                  confirmLabel: "Rattacher",
                  onConfirm: () => run(() => api.post(`${base}/attach-orphan`,
                    { orphan_tracker_id: o.tracker_id, target_uid: sel }), "Fiche orpheline rattachée"),
                })}
                className={`${actionBtn} border-blue-200 text-blue-700 hover:bg-blue-50`} data-testid={`recon-orphan-attach-${o.tracker_id}`}>
                <Link2 size={13} /> Rattacher…
              </button>
            </div>
          );
        })}
      </Card>

      {/* Boîtiers manquants → réaffecter un boîtier ou archiver */}
      <Card className="p-4" data-testid="recon-missing-card">
        <SectionTitle count={data.tracker_missing.length}>Véhicules au boîtier manquant</SectionTitle>
        {data.tracker_missing.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="recon-missing-empty">Aucun véhicule au boîtier manquant.</p>
        ) : (
          <>
            <p className="text-[10px] text-gray-400 mb-2">
              Boîtiers réaffectables : uniquement les boîtiers non liés à une fiche garage Navixy (la synchronisation suit le garage) et sans données propres.
            </p>
            {data.tracker_missing.map((v) => {
              const sel = reassignSel[v.uid] || "";
              const choices = reassignable.filter((r) => r.holder_uid !== v.uid);
              return (
                <div key={v.uid} className="flex flex-wrap items-center gap-3 py-2.5 border-b border-gray-50 last:border-0" data-testid={`recon-missing-row-${v.uid}`}>
                  <VehicleCell v={v} />
                  <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">Boîtier manquant depuis {fmtDateTime(v.updated_at)}</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <select value={sel} onChange={(e) => setReassignSel({ ...reassignSel, [v.uid]: e.target.value })}
                      className={selCls} data-testid={`recon-missing-tracker-select-${v.uid}`}>
                      <option value="">Nouveau boîtier…</option>
                      {choices.map((r) => (
                        <option key={r.tracker_id} value={r.tracker_id} disabled={r.holder_has_data}>
                          Boîtier {r.tracker_id} — {r.label || "sans nom"}{r.holder_has_data ? " — porte des données" : ""}
                        </option>
                      ))}
                    </select>
                    <button disabled={!sel || busy}
                      onClick={() => setConfirm({
                        title: "Réaffecter le boîtier",
                        lines: [
                          `Le boîtier ${sel} sera rattaché à « ${v.label || v.uid} ». Le véhicule redevient actif.`,
                          "L'entrée technique actuelle du boîtier (coquille sans données) sera archivée — réversible, auditée.",
                          "La fiche et les documents suivent le véhicule ; aucune donnée supprimée, aucune écriture vers Navixy.",
                        ],
                        confirmLabel: "Réaffecter",
                        onConfirm: () => run(() => api.post(`${base}/reassign-tracker`,
                          { uid: v.uid, tracker_id: parseInt(sel, 10) }), "Boîtier réaffecté"),
                      })}
                      className={`${actionBtn} border-blue-200 text-blue-700 hover:bg-blue-50`} data-testid={`recon-missing-reassign-${v.uid}`}>
                      <ArrowRightLeft size={13} /> Réaffecter…
                    </button>
                    <button disabled={busy}
                      onClick={() => setConfirm({
                        title: "Archiver le véhicule",
                        danger: true,
                        lines: [
                          `« ${v.label || v.uid} » sera masqué du Dashboard client (KPI, listes, actions, PDF).`,
                          "Fiche, documents et historique sont conservés — désarchivage possible à tout moment.",
                          "Aucune donnée n'est supprimée. Action auditée.",
                        ],
                        confirmLabel: "Archiver",
                        onConfirm: () => run(() => api.post(`${base}/archive`, { uid: v.uid }), "Véhicule archivé — données conservées"),
                      })}
                      className={`${actionBtn} border-red-200 text-red-600 hover:bg-red-50`} data-testid={`recon-missing-archive-${v.uid}`}>
                      <Archive size={13} /> Archiver…
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </Card>

      {/* Archivés → désarchiver */}
      <Card className="p-4" data-testid="recon-archived-card">
        <SectionTitle count={data.archived.length}>Véhicules archivés</SectionTitle>
        {data.archived.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="recon-archived-empty">Aucun véhicule archivé.</p>
        ) : data.archived.map((v) => (
          <div key={v.uid} className="flex flex-wrap items-center gap-3 py-2.5 border-b border-gray-50 last:border-0" data-testid={`recon-archived-row-${v.uid}`}>
            <VehicleCell v={v} />
            <span className="text-[10px] text-gray-400">
              Archivé le {fmtDateTime(v.archived_at)}{v.archived_by ? ` par ${v.archived_by}` : ""}{v.archive_reason ? ` — ${v.archive_reason}` : ""}
            </span>
            <button disabled={busy}
              onClick={() => setConfirm({
                title: "Désarchiver le véhicule",
                lines: [
                  `« ${v.label || v.uid} » repasse en « boîtier manquant » et sera réactivé automatiquement si son boîtier réapparaît chez Navixy.`,
                  "Action auditée, réversible.",
                ],
                confirmLabel: "Désarchiver",
                onConfirm: () => run(() => api.post(`${base}/unarchive`, { uid: v.uid }), "Véhicule désarchivé"),
              })}
              className={`${actionBtn} border-gray-200 text-gray-700 hover:bg-gray-50 ml-auto`} data-testid={`recon-unarchive-${v.uid}`}>
              <ArchiveRestore size={13} /> Désarchiver…
            </button>
          </div>
        ))}
      </Card>

      {/* Suggestions de doublons — lecture seule, jamais de fusion automatique */}
      <Card className="p-4" data-testid="recon-duplicates">
        <SectionTitle count={data.duplicate_suggestions.length}>Suggestions de doublons (plaque identique)</SectionTitle>
        <p className="text-[10px] text-gray-400 mb-2 flex items-center gap-1">
          <Copy size={11} /> Affichage seulement — LOGITRAK ne fusionne jamais automatiquement des véhicules (plaque, VIN ou nom identiques).
        </p>
        {data.duplicate_suggestions.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="recon-duplicates-empty">Aucune plaque en double dans le registre.</p>
        ) : data.duplicate_suggestions.map((g) => (
          <div key={g.reg_number} className="py-2 border-b border-gray-50 last:border-0">
            <div className="text-xs font-semibold text-gray-800">{g.reg_number}</div>
            {g.vehicles.map((x) => (
              <div key={x.uid} className="text-[11px] text-gray-500 ml-3">
                {x.label || x.uid} · boîtier {x.tracker_id ?? "—"} · {x.status}{x.navixy_vehicle_id ? ` · garage ${x.navixy_vehicle_id}` : ""}
              </div>
            ))}
          </div>
        ))}
      </Card>

      {data.documents_without_uid > 0 && (
        <p className="text-[10px] text-gray-400" data-testid="recon-docs-without-uid">
          {data.documents_without_uid} document(s) V2 sans identité véhicule — rattachables depuis l'écran Documents (référence véhicule).
        </p>
      )}

      {/* Dialogue de confirmation — obligatoire avant toute action */}
      {confirm && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => !busy && setConfirm(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <Card className="p-5 w-full max-w-md pointer-events-auto" data-testid="recon-confirm-dialog">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <AlertTriangle size={15} className={confirm.danger ? "text-red-500" : "text-amber-500"} /> {confirm.title}
              </h3>
              <div className="space-y-1.5 mb-4">
                {confirm.lines.map((l, i) => <p key={i} className="text-xs text-gray-600">{l}</p>)}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirm(null)} disabled={busy}
                  className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50" data-testid="recon-confirm-cancel">
                  Annuler
                </button>
                <button onClick={confirm.onConfirm} disabled={busy}
                  className={`text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${confirm.danger ? "bg-red-600 hover:bg-red-700" : "bg-[#1e6ae5]"}`}
                  data-testid="recon-confirm-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : (confirm.confirmLabel || "Confirmer")}
                </button>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
